import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  Box, Paper,
  Snackbar, IconButton
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { Skeleton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DetailsPageLowerLayer from '../../DetailsPageLowerLayer/DetailsPageLowerLayer';
import TopSection from '../../../CommonComponents/TopSection';
import { fetchAllReports } from "../../../services/UserRole/ReportsService";
import { APPLICATION_LABEL_MAP } from '../../../constants/userAccess';
import ApplicationDetails from './ApplicationDetails';
import UserSpecificDetails from './UserSpecificDetails';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const SummaryCardSkeleton = () => (
  <Paper
    elevation={2}
    sx={{
      px: 1,
      py: 1,
      minWidth: 170,
      height: 55,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Skeleton variant="text" width="70%" height={18} />
    <Skeleton variant="text" width="40%" height={16} />
  </Paper>
);

const SummaryCarousel = ({ cards = [], loading }) => {
  const containerRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const ITEMS_PER_SLIDE = 6;
  const CARD_WIDTH = 170 + 16;
  const SCROLL_AMOUNT = ITEMS_PER_SLIDE * CARD_WIDTH;
  const skeletonCards = Array.from({ length: ITEMS_PER_SLIDE });

  const updateScrollState = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const tolerance = 5; // (pixel rounding)

    setCanScrollLeft(el.scrollLeft > tolerance);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - tolerance);
  }, []);

  const scrollLeft = () => {
    const el = containerRef.current;
    if (!el) return;

    el.scrollBy({ left: -SCROLL_AMOUNT, behavior: "smooth" });
    setTimeout(updateScrollState, 300);
  };

  const scrollRight = () => {
    const el = containerRef.current;
    if (!el) return;

    el.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
    setTimeout(updateScrollState, 300);
  };

  useEffect(() => {
    updateScrollState();

    const el = containerRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [cards, updateScrollState]);

  return (
    <Box display="flex" alignItems="center" width="100%">
      {/* LEFT ARROW */}
      <IconButton
        onClick={scrollLeft}
        disabled={!canScrollLeft}
        sx={{
          color: "#000",
          mx: -1,
          paddingRight: 3,
          opacity: canScrollLeft ? 1 : 0.3,
          pointerEvents: canScrollLeft ? "auto" : "none",
        }}
      >
        <ChevronLeftIcon />
      </IconButton>

      {/* CAROUSEL */}
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          gap: 4.5,
          overflowX: "auto",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          flex: 1,
        }}
      >
        {loading
          ? skeletonCards.map((_, idx) => (
            <Box key={idx} minWidth={170}>
              <SummaryCardSkeleton />
            </Box>
          ))
          : cards.map((card, index) => (
            <Box key={index} minWidth={170}>
              <SummaryCard {...card} />
            </Box>
          ))}
      </Box>

      {/* RIGHT ARROW */}
      <IconButton
        onClick={scrollRight}
        disabled={!canScrollRight}
        sx={{
          color: "#000",
          mx: -1,
          paddingLeft: 3,
          opacity: canScrollRight ? 1 : 0.3,
          pointerEvents: canScrollRight ? "auto" : "none",
        }}
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  );
};

const normalizeApiData = (data) =>
  data.map((item, index) => ({
    id: index,
    userName: item.userName,
    userEmail: item.userEmail,
    commandCenter: item.commandCenter,
    ssa: item.ssa,
    invRb: item.invRb,
    mpLa: item.mpLa,
    mpCm: item.mpCm,
    mpScb: item.mpScb,
    // mpScm: item.mpScm,
    mpPom: item.mpPom,
    mpGr: item.mpGr,
    trOp: item.trOp
  }));

const normalizeCountsData = (data) => Number(data) || 0;

const SummaryCard = ({ label, value, onClick, active }) => {
  const isAll = label === "All";
  const isZero = value === 0;

  return (
    <Paper
      onClick={isZero ? undefined : onClick}
      //onClick={onClick}
      elevation={2}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        px: 1,
        py: 1,
        border: "0.5px solid #0b3c5d",
        backgroundColor: isAll ? "#284d5a" : "#fff",
        textAlign: "center",
        minWidth: 170,
        height: 55,
        cursor: isAll || isZero ? "not-allowed" : "pointer",

        "&:hover": {
          backgroundColor: isAll ? "#284d5a" : isZero ? "#fff" : "#000"
        },
        "& .card-label": {
          color: isAll ? "#b8b8b6" : "#000",
          fontSize: 13.5,
          fontWeight: 700
        },
        "&:hover .card-label": {
          color: isAll ? "#b8b8b6" : isZero ? "#000" : "#fff",
        },
        "& .card-value": {
          fontSize: 13.5,
          paddingTop: "2px",
          color: isAll ? "#fff" : "#1976d2"
        },
        "&:hover .card-value": {
          color: isAll ? "#fff" : isZero ? "#1976d2" : "#b8b8b6",
        },
      }}
    >
      <div className="card-label">
        {label}
      </div>
      <div className="card-value">
        {value}
      </div>
    </Paper>
  )
};

const UserAccessReport = () => {
  const breadcrumbArray = ["User Access Management", "Report"];
  const [rows, setRows] = useState([]);
  const [countsData, setCountsData] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeScreen, setActiveScreen] = useState("ALL");
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const CellLinkRenderer = useCallback((params) => {
    const { value, data, colDef } = params;
    const isYes = value === "Yes";

    const handleClick = (e) => {
      e.stopPropagation();
      if (!isYes) return;

      setSelectedUser({
        application: colDef.headerName,
        userName: data.userName,
        userEmail: data.userEmail,
      });
      setActiveScreen("USER_SPECIFIC");
    }

    return (
      <span
        style={{
          color: isYes ? "#1976d2" : "#000",
          textDecoration: isYes ? "underline" : "none",
          cursor: isYes ? "pointer" : "default",
        }}
        onClick={handleClick}
      >
        {value}
      </span>
    );
  }, []);

  const SummaryGrid = ({ rows }) => {
    const reportTab = useMemo(() => ({
      label: "Report",
      loading: loading,
      columns: [
        {
          field: "userName", headerName: "User Name", flex: 1,
        },
        { field: "userEmail", headerName: "User Email", flex: 1.3 },
        {
          field: "commandCenter", headerName: "Command Center", flex: 0.9,
          cellRenderer: CellLinkRenderer
        },
        { field: "ssa", headerName: "IO - Safety Stock Approval", flex: 0.5, minWidth: 250, cellRenderer: CellLinkRenderer },
        { field: "invRb", headerName: "CS - Inventory Rebalancing", flex: 0.5, minWidth: 250, cellRenderer: CellLinkRenderer },
        { field: "mpLa", headerName: "MP - Lead time Analytics", flex: 0.5, minWidth: 230, cellRenderer: CellLinkRenderer },
        { field: "mpCm", headerName: "MP - AL Contract Management", flex: 0.5, minWidth: 270, cellRenderer: CellLinkRenderer },
        { field: "mpScb", headerName: "MP - AL Supplier Collaboration/Confirmation", flex: 0.5, minWidth: 350, cellRenderer: CellLinkRenderer },
        // { field: "mpScm", headerName: "MP - Supplier Confirmation", flex: 0.5, minWidth: 260, cellRenderer: CellLinkRenderer },
        { field: "mpPom", headerName: "MP - PO Maintenance", flex: 0.5, minWidth: 210, cellRenderer: CellLinkRenderer },
        { field: "mpGr", headerName: "MP - GR Processing Time Analytics", flex: 0.5, minWidth: 300, cellRenderer: CellLinkRenderer },
        { field: "trOp", headerName: "OM - Load Optimization", flex: 0.5, minWidth: 270, cellRenderer: CellLinkRenderer },
      ],
      rows,
    }), [rows]);
    return <DetailsPageLowerLayer tabs={[reportTab]} hideTabs />;
  };

  const renderScreen = () => {
    if (activeScreen === "ALL") {
      return <SummaryGrid rows={rows} />;
    }
    if (activeScreen === "USER_SPECIFIC") {
      return (
        <UserSpecificDetails
          application={selectedUser?.application}
          userName={selectedUser?.userName}
          userEmail={selectedUser?.userEmail}
          onBack={() => setActiveScreen("ALL")}
        />
      );
    }
    return (
      <ApplicationDetails application={APPLICATION_LABEL_MAP[activeScreen]} onBack={() => setActiveScreen("ALL")} />)
  }

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const data = await fetchAllReports(activeScreen, "", activeScreen);
        setRows(normalizeApiData(data[0].values));
        setCountsData(data[0].counts);
      } catch (error) {
        console.error(error);
        setSnackbar({
          open: true,
          message: "Failed to load reports",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  return (
    <div>
      <TopSection
        breadcrumbArray={breadcrumbArray}
        hideHeader
        hideTitle
      />

      {activeScreen === "ALL" && (
        <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: "#ddd" }}>
          <SummaryCarousel
            loading={loading}
            cards={[
              { label: "All", value: normalizeCountsData(countsData.all) },
              { label: "Command Center", value: normalizeCountsData(countsData.commandCenter), onClick: () => setActiveScreen("COMMANDCENTER") },
              { label: "IO - Safety Stock Approval", value: normalizeCountsData(countsData.ssa), onClick: () => setActiveScreen("SSA") },
              { label: "CS - Inventory Rebalancing", value: normalizeCountsData(countsData.invRb), onClick: () => setActiveScreen("INVENTORY_REBALANCING") },
              { label: "MP - Lead time Analytics", value: normalizeCountsData(countsData.mpLa), onClick: () => setActiveScreen("LEAD_TIME_ANALYTICS") },
              { label: "MP - AL Contract Management", value: normalizeCountsData(countsData.mpCm), onClick: () => setActiveScreen("AL_CONTRACT_MANAGEMENT") },
              // { label: "MP - Supplier Confirmation", value: normalizeCountsData(countsData.mpScm), onClick: () => setActiveScreen("SUPPLIER_CONFIRMATION") },
              { label: "MP - AL Supplier Collaboration/Confirmation", value: normalizeCountsData(countsData.mpScb), onClick: () => setActiveScreen("AL_SUPPLIER_COLLABORATION") },
              { label: "MP - PO Maintenance", value: normalizeCountsData(countsData.mpPom), onClick: () => setActiveScreen("PO_MAINTENANCE") },
              { label: "MP - GR Processing Time Analytics", value: normalizeCountsData(countsData.mpGr), onClick: () => setActiveScreen("GRPT") },
              { label: "OM - Load Optimization", value: normalizeCountsData(countsData.trOp), onClick: () => setActiveScreen("OM_LOAD_OPTIMISATION") },
            ]}
          />
        </Paper>
      )}

      {/* SCREEN CHANGES BELOW */}
      {renderScreen()}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
export default UserAccessReport;